import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import Dialog from "../components/dashboard/Dialog";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AdminBadges() {
  const { status } = useSession();
  const navigate = useNavigate();
  
  const [badges, setBadges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    icon: "military_tech",
    description: "",
    pointsThreshold: 100
  });

  const fetchBadges = async () => {
    try {
      const res = await api.get('/admin/badges');
      const sortedData = res.data.sort((a: any, b: any) => a.pointsThreshold - b.pointsThreshold);
      setBadges(sortedData);
    } catch (err) {
      console.error("Failed to fetch badges:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
       navigate("/login");
    } else if (status === "authenticated") {
       fetchBadges();
    }
  }, [status, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        pointsThreshold: isNaN(formData.pointsThreshold) ? 0 : formData.pointsThreshold
      };

      if (selectedBadge) {
        await api.patch(`/admin/badges/${selectedBadge.id}`, payload);
      } else {
        await api.post('/admin/badges', payload);
      }
      
      setIsModalOpen(false);
      setFormData({ name: "", icon: "military_tech", description: "", pointsThreshold: 100 });
      setSelectedBadge(null);
      fetchBadges();
    } catch (err) {
      console.error("Transmission failure:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedBadge) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/admin/badges/${selectedBadge.id}`);
      setIsDeleteModalOpen(false);
      setSelectedBadge(null);
      fetchBadges();
    } catch (err) {
      console.error("Deletion failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (badge: any) => {
     setSelectedBadge(badge);
     setFormData({
        name: badge.name,
        icon: badge.icon,
        description: badge.description,
        pointsThreshold: badge.pointsThreshold
     });
     setIsModalOpen(true);
  };

  const TACTICAL_ICONS = [
    // Achievements
    "military_tech", "workspace_premium", "stars", "emoji_events", "rewarded_ads", "social_leaderboard", "leaderboard",
    // Tactical & Energy
    "target", "rocket_launch", "bolt", "auto_awesome", "diamond", "local_fire_department", "electric_bolt", "flash_on", "rocket", "energy_savings_leaf",
    // Security & Logic
    "groups", "verified", "shield", "flag", "security", "lock_person", "policy", "assured_workload", "verified_user",
    // Mastery & Science
    "school", "menu_book", "history_edu", "psychology", "science", "biotech", "architecture", "design_services", "auto_stories",
    // Metrics & Ops
    "monitoring", "speed", "trending_up", "show_chart", "query_stats", "insights", "analytics", "bar_chart", "timeline",
    // Equipment & Tools
    "build", "settings_accessibility", "handyman", "construction", "precision_manufacturing", "layers"
  ];

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      
      <main className="ml-0 md:ml-20 pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6">
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Achievement Engine</span>
              <h1 className="text-3xl sm:text-5xl font-black text-on-surface tracking-tighter leading-tight">
                Tactical <span className="text-outline-variant/40">Badges.</span>
              </h1>
            </div>
            <button 
              onClick={() => {
                setSelectedBadge(null);
                setFormData({ name: "", icon: "military_tech", description: "", pointsThreshold: 100 });
                setIsModalOpen(true);
              }}
              className="w-full sm:w-auto px-6 py-4 bg-primary text-surface rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all active:scale-95 shadow-xl shadow-primary/20 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">add_task</span>
              Define Blueprint
            </button>
          </div>

          {/* Badge Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-surface-container-low rounded-[1.75rem] sm:rounded-[2.5rem] animate-pulse border border-outline-variant/5" />
              ))
            ) : badges.length > 0 ? badges.map((badge) => (
              <div key={badge.id} className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col justify-between min-h-[280px]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-primary/10 transition-colors" />
                
                <div>
                  <div className="flex justify-between items-start mb-6 relative">
                    <div className="w-14 h-14 bg-surface-container-high rounded-2xl flex items-center justify-center text-primary border border-outline-variant/10 shadow-inner group-hover:scale-110 transition-transform">
                      <span className="material-symbols-outlined text-3xl">{badge.icon}</span>
                    </div>
                    <div className="flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                       <button 
                         onClick={() => handleEdit(badge)}
                         className="w-8 h-8 rounded-lg bg-surface-container-high flex items-center justify-center text-slate-500 hover:text-primary transition-colors border border-outline-variant/10"
                       >
                          <span className="material-symbols-outlined text-sm">edit</span>
                       </button>
                       <button 
                         onClick={() => {
                           setSelectedBadge(badge);
                           setIsDeleteModalOpen(true);
                         }}
                         className="w-8 h-8 rounded-lg bg-red-500/5 flex items-center justify-center text-red-500 hover:bg-red-500/10 transition-all border border-red-500/10"
                       >
                          <span className="material-symbols-outlined text-sm">delete</span>
                       </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                     <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tight truncate">{badge.name}</h3>
                     <p className="text-xs font-medium text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed min-h-[48px]">{badge.description}</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-outline-variant/10 flex items-center justify-between">
                   <div className="flex flex-col min-w-0">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Activation Lock</span>
                      <span className="text-xs font-bold text-primary truncate">{badge.pointsThreshold} Point Milestone</span>
                   </div>
                   <div className="w-8 h-8 bg-surface-container-high rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[14px] text-slate-400">lock_open</span>
                   </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full py-32 text-center space-y-4 opacity-20">
                 <span className="material-symbols-outlined text-6xl">military_tech</span>
                 <p className="text-xl font-black tracking-tight italic">No business statuses have been commissioned yet.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Blueprint Modal */}
      <Dialog 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={selectedBadge ? "Edit Business Status" : "Create New Business Status"}
      >
        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Tactical Insignia Selection</label>
            <div className="p-4 sm:p-6 bg-surface-container-low rounded-[1.5rem] sm:rounded-[2rem] border border-outline-variant/10 flex flex-col gap-6">
              <div 
                className="flex items-center gap-6 cursor-pointer group"
                onClick={() => setIsIconPickerOpen(!isIconPickerOpen)}
              >
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center border transition-all ${
                  isIconPickerOpen ? "bg-primary text-white border-primary shadow-xl shadow-primary/20" : "bg-surface-container-high text-primary border-outline-variant/10 shadow-inner"
                }`}>
                  <span className="material-symbols-outlined text-3xl sm:text-4xl leading-none">{formData.icon || 'military_tech'}</span>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-on-surface">Active Blueprint</p>
                    <span className={`material-symbols-outlined text-[14px] transition-transform duration-500 ${isIconPickerOpen ? "rotate-180 text-primary" : "text-slate-300"}`}>
                      keyboard_arrow_down
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                    Click to {isIconPickerOpen ? "close" : "open"} the vault. Current: <span className="font-black text-primary uppercase">{formData.icon}</span>
                  </p>
                </div>
              </div>

              {isIconPickerOpen && (
                <div className="space-y-6 pt-4 border-t border-outline-variant/5 animate-in fade-in zoom-in-95 duration-500">
                  <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto custom-scrollbar p-1">
                    {TACTICAL_ICONS.map((icon, idx) => (
                      <button
                        key={`${icon}-${idx}`}
                        type="button"
                        onClick={() => {
                          setFormData({...formData, icon});
                          setIsIconPickerOpen(false);
                        }}
                        className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                          formData.icon === icon 
                            ? "bg-primary text-white shadow-lg shadow-primary/20 scale-110 z-10" 
                            : "bg-surface-container-high text-slate-400 hover:text-primary hover:bg-primary/5 border border-outline-variant/10"
                        }`}
                      >
                        <span className="material-symbols-outlined text-lg">{icon}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tactical Override</p>
                    <input 
                      type="text" 
                      placeholder="Custom Icon Name..."
                      className="w-full px-4 py-3 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-[10px] font-black uppercase tracking-widest"
                      value={formData.icon}
                      onChange={(e) => setFormData({...formData, icon: e.target.value.toLowerCase()})}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
             <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Status Name</label>
                <input 
                  required
                  type="text" 
                  placeholder="e.g. Master Executor"
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
             </div>

             <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Unlock Milestone (Points)</label>
                <input 
                  required
                  type="number" 
                  placeholder="e.g. 500"
                  className="w-full px-5 py-3.5 bg-surface-container-high text-primary border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-black"
                  value={formData.pointsThreshold}
                  onChange={(e) => setFormData({...formData, pointsThreshold: parseInt(e.target.value) || 0})}
                />
             </div>

             <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Narrative Description</label>
                <textarea 
                  required
                  rows={3}
                  placeholder="Awarded to users who maintain exceptional tactical discipline..."
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
             </div>
          </div>

          <div className="pt-2">
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 sm:py-5 bg-primary text-surface rounded-[1.25rem] sm:rounded-[2rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-surface/30 border-t-surface rounded-full animate-spin" />
                  Syncing Blueprint...
                </>
              ) : "Finalize Protocol"}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        title="Retire Business Status?"
        className="!max-w-md border-red-500/20"
      >
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/10 text-red-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl sm:text-4xl">warning</span>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tight">Retiring Business Status</h4>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2">
              You are about to decommission the <span className="text-on-surface font-black italic">{selectedBadge?.name}</span> status. Existing associations will persist, but no new status assignments will be issued.
            </p>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button 
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Abeyance
            </button>
            <button 
              onClick={confirmDelete}
              disabled={isSubmitting}
              className="flex-1 py-3.5 bg-red-500 text-white rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl hover:shadow-red-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                 <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : "Retire Medal"}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
